"use client";

import {
  CheckCircle,
  Loader,
  MessageSquare,
  Plus,
  PlusCircle,
  Search,
  X,
  Clock,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import ChatInput from "./ChatInput";
import MobileMenu from "./MobileMenu";
import { useNavigate, useSearchParams } from "react-router-dom";
import MessageList from "./MessageList";
import StreamingMessage from "./StreamingMessage";
import {
  sendMessage,
  sendImageMessage,
  sendPdfMessage,
} from "../../services/messageApi";
import {
  createConversationApi,
  updateConversationApi,
  deleteConversationApi,
} from "../../services/conversationApi";
import { toast } from "react-toastify";
import { useAuthStore } from "../hooks/useAuth";

import systemImage from "../assets/system.png";
import { useToken } from "../context/TokenContext";
import TokenAlertModal from "./TokenAlertModal";
import EditableTitle from "./EditableTitle";
import { useAuth } from "../context/AuthContext";

const CentreArea = memo(
  ({
    selectedChatId,
    setChats,
    setAllChats,
    allChats,
    setSelectedChatId,
    messages,
    setMessages,
    onNewChat,
    fetching,
    isNewChat,
    setIsNewChat,
    selectedModel,
    allCustomModels,
    customModelId,
    setCustomModelId,
    onChatSelect,
    onStreamingChange,
  }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [messageChatId, setMessageChatId] = useState(null);
    const [sending, setSending] = useState(false);
    const [typingText, setTypingText] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [currentStreamingMessage, setCurrentStreamingMessage] =
      useState(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const chatInputRef = useRef(null);

    const [isStartingChat, setIsStartingChat] = useState(false);
    const [isBotSwitching, setIsBotSwitching] = useState(false);
    const [chatSearchQuery, setChatSearchQuery] = useState("");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [chatToDelete, setChatToDelete] = useState(null);
    const [isDeletingChat, setIsDeletingChat] = useState(false);
    const { token, user } = useAuthStore();
    const { userData } = useAuth();

    const messagesEndRef = useRef(null);
    const streamingMessageRef = useRef(null);

    const scrollToBottom = useCallback(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const { refreshTokenData, getDisplayMessage } = useToken();
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSearch = useCallback(async () => {
      try {
        // Search functionality placeholder
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error("Search failed:", error);
      }
    }, []);

    useEffect(() => {
      const handleKeyPress = (event) => {
        const activeElement = document.activeElement;
        const isInputActive =
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.contentEditable === "true");

        // Don't auto-focus if modifier keys are pressed or special keys
        const isModifierKey = event.ctrlKey || event.metaKey || event.altKey;
        const isSpecialKey = [
          "Tab",
          "Enter",
          "Escape",
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Backspace",
          "Delete",
          "Home",
          "End",
          "PageUp",
          "PageDown",
          "F1",
          "F2",
          "F3",
          "F4",
          "F5",
          "F6",
          "F7",
          "F8",
          "F9",
          "F10",
          "F11",
          "F12",
        ].includes(event.key);

        if (
          !isInputActive &&
          !isModifierKey &&
          !isSpecialKey &&
          !isNewChat &&
          !sending &&
          !isStreaming &&
          event.key.length === 1 &&
          chatInputRef.current
        ) {
          chatInputRef.current.focus();
        }
      };

      // Add event listener to document
      document.addEventListener("keydown", handleKeyPress);

      // Cleanup
      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }, [isNewChat, sending, isStreaming]);

    // Memoize model selection logic
    const currentModelId = useMemo(() => {
      // If a specific model is selected, use it
      if (customModelId?.id) {
        return customModelId.id;
      }

      // If we have models but no specific selection, use the first one
      if (allCustomModels.length > 0) {
        return allCustomModels[0].id;
      }

      // If we have a selected model from URL params, use it
      if (selectedModel && selectedModel._id) {
        return selectedModel._id;
      }

      // If we have a selected chat with a model, use that model
      if (selectedChatId?.usedCustomModel?._id) {
        return selectedChatId.usedCustomModel._id;
      }

      // No valid model found
      return null;
    }, [customModelId, allCustomModels, selectedModel, selectedChatId]);

    // Optimize scroll behavior with proper cleanup and debouncing
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        scrollToBottom();
      }, 100);

      return () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, [messages.length, isStreaming, scrollToBottom]);

    useEffect(() => {
      handleSearch();
    }, [handleSearch]);

    useEffect(() => {
      if (selectedModel && !customModelId) {
        const modelOption = {
          id: selectedModel._id,
          label: selectedModel.title,
          value: selectedModel.modelHandle,
        };
        setCustomModelId(modelOption);
      }
    }, [selectedModel, customModelId, setCustomModelId]);

    // Handle bot switching loading state
    useEffect(() => {
      if (customModelId?.id) {
        setIsBotSwitching(true);
        const timer = setTimeout(() => {
          setIsBotSwitching(false);
        }, 500); // Short delay for smooth transition

        return () => clearTimeout(timer);
      }
    }, [customModelId?.id]);

    useEffect(() => {
      onStreamingChange?.(isStreaming || sending);
    }, [isStreaming, sending, onStreamingChange]);

    const formatTime = useCallback(
      (ts) =>
        new Date(ts).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      [],
    );

    // Memoize props for child components
    const messageListProps = useMemo(
      () => ({
        messages,
        user,
        formatTime,
        systemImage,
      }),
      [messages, user, formatTime],
    );

    const streamingMessageProps = useMemo(
      () => ({
        currentStreamingMessage,
        typingText,
        formatTime,
        systemImage,
      }),
      [currentStreamingMessage, typingText, formatTime],
    );

    // Simplified container styling without heavy effects
    const containerClassName =
      "relative flex  flex-col h-[calc(100vh-98px)] md:h-[calc(100vh-42px)] w-full bg-white/90 rounded-xl lg:rounded-2xl  border border-gray-200/20 shadow-sm";

    // Function to automatically generate chat titles based on conversation
    function toSentenceCase(str) {
      if (!str) return "";

      // Trim spaces and convert everything to lowercase
      str = str.trim().toLowerCase();

      // Capitalize only the first character
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
    const handleStartNewChat = useCallback(async () => {
      if (isStartingChat) return;

      // Validate that we have a valid model ID before creating a chat
      if (!currentModelId) {
        console.error("No custom model selected for new chat");
        toast.error("Please select a custom bot before creating a new chat");
        return;
      }

      setIsStartingChat(true);

      try {
        // Call the parent function to update state
        onNewChat();

        // Use memoized current model ID
        const modelId = currentModelId;

        // Create a new conversation first
        const conversationData = {
          conversationName: "New Chat",
          usedCustomModel: modelId,
          clientId: userData?.activeClientId,
        };

        const conversationResult =
          await createConversationApi(conversationData);

        if (conversationResult.success) {
          const newConversation = {
            _id: conversationResult.results.conversationId,
            title: conversationResult.results.conversationTitle,
            createdAt: new Date().toISOString(),
            modelHandle:
              conversationResult.results.customModelData ||
              allCustomModels[0]?.value,
          };
          // Add the new conversation to the sidebar
          setChats((prev) => [newConversation, ...prev]);
          setAllChats((prev) => [newConversation, ...prev]);
          setSelectedChatId(newConversation);
          setMessages([]);

          onChatSelect(newConversation, false);

          // Update URL with chatId and botId
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set("chatId", newConversation._id);
          if (currentModelId) {
            newSearchParams.set("botId", currentModelId);
          }
          navigate(
            `/dashboard/${newConversation._id}?${newSearchParams.toString()}`,
            { replace: true },
          );
        } else {
          throw new Error("Failed to create conversation");
        }
      } catch (error) {
        console.error("Failed to start new chat:", error);
        toast.error("Failed to start new chat");
      } finally {
        setIsStartingChat(false);
      }
    }, [
      isStartingChat,
      onNewChat,
      currentModelId,
      setChats,
      setAllChats,
      setSelectedChatId,
      setMessages,
      navigate,
      allCustomModels,
      searchParams,
    ]);

    const handleSendMessage = useCallback(
      async (message, messageType) => {
        if (sending || isStreaming) return;

        setIsNewChat(false);

        if (!currentModelId) {
          console.error("No custom model selected");
          const errorMessage = {
            id: `error-${Date.now()}`,
            role: "system",
            text: "Please select a custom bot before sending a message.",
            timestamp: new Date().toISOString(),
            error: true,
          };
          setMessages((prev) => [...prev, errorMessage]);
          return;
        }
        let userMessage;

        if (messageType === "image") {
          const previewUrl =
            message instanceof File ? URL.createObjectURL(message) : null;
          userMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            text: null,
            type: "image",
            image: previewUrl,
            timestamp: new Date().toISOString(),
          };
        } else if (["pdf", "docx", "csv"].includes(messageType)) {
          const fileUrl =
            message instanceof File ? URL.createObjectURL(message) : null;
          userMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            text: message instanceof File ? message.name : null,
            type: messageType,
            pdfFile: messageType === "pdf" ? fileUrl : null,
            docxFile: messageType === "docx" ? fileUrl : null,
            csvFile: messageType === "csv" ? fileUrl : null,
            fileName: message instanceof File ? message.name : null,
            timestamp: new Date().toISOString(),
          };
        } else {
          userMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            text: message,
            type: messageType || "text",
            image: null,
            timestamp: new Date().toISOString(),
          };
        }

        setMessages((prev) => [...prev, userMessage]);
        setSending(true);
        setIsStreaming(true);
        setTypingText("");

        // Create placeholder for assistant response
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          role: "system",
          text: "",
          timestamp: new Date().toISOString(),
        };

        setCurrentStreamingMessage(assistantMessage);
        streamingMessageRef.current = assistantMessage;

        try {
          setMessageChatId(selectedChatId?._id || null);
          const onChunk = (chunk) => {
            setTypingText((prev) => prev + chunk);
            if (streamingMessageRef.current) {
              streamingMessageRef.current.text += chunk;
            }
          };
          const onComplete = ({
            fullResponse,
            newConversation,
            conversationTitle,
          }) => {
            const finalText = fullResponse || streamingMessageRef.current.text;

            const finalMessage = {
              ...streamingMessageRef.current,
              text: finalText,
            };

            if (newConversation) {
              setChats((prev) => [
                {
                  _id: newConversation._id,
                  title: newConversation.title,
                  createdAt: newConversation.createdAt,
                  modelHandle: newConversation.modelHandle,
                },
                ...prev,
              ]);

              setSelectedChatId(newConversation);

              // Update URL with chatId and botId
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set("chatId", newConversation._id);
              if (currentModelId) {
                newSearchParams.set("botId", currentModelId);
              }
              navigate(
                `/dashboard/${
                  newConversation._id
                }?${newSearchParams.toString()}`,
                { replace: true },
              );
            } else if (selectedChatId.title == "New Chat") {
              setChats((prev) => {
                return prev.map((chat) => {
                  if (chat._id === selectedChatId?._id) {
                    return { ...chat, title: conversationTitle };
                  }
                  return chat;
                });
              });
              setAllChats((prev) => {
                return prev.map((chat) => {
                  if (chat._id === selectedChatId?._id) {
                    return { ...chat, title: conversationTitle };
                  }
                  return chat;
                });
              });
            }

            // Clear streaming state first, then add final message
            setCurrentStreamingMessage(null);
            setTypingText("");
            setIsStreaming(false);
            setSending(false);
            streamingMessageRef.current = null;

            // Add final message after clearing streaming state
            setMessages((prev) => {
              const newMessages = [...prev, finalMessage];
              return newMessages;
            });
          };

          const onError = (error) => {
            console.error("Streaming error:", error);
            setCurrentStreamingMessage(null);
            setTypingText("");
            setIsStreaming(false);
            setSending(false);
            streamingMessageRef.current = null;
          };
          const tokenCheck = await refreshTokenData();

          if (!tokenCheck.success) {
            const displayMessage = getDisplayMessage();

            if (user?.planType === "free_plan") {
              setModalTitle("Upgrade Required");
            } else {
              setModalTitle("Credit Limit Reached");
            }
            setModalMessage(displayMessage);
            setIsModalOpen(true);
            setCurrentStreamingMessage(null);
            setTypingText("");
            setIsStreaming(false);
            setSending(false);
            streamingMessageRef.current = null;
            return;
          }
          if (messageType === "image" && message instanceof File) {
            const formData = new FormData();
            formData.append("image", message);
            if (selectedChatId?._id)
              formData.append("conversationId", selectedChatId._id);
            formData.append("customModelId", currentModelId);
            await sendImageMessage(formData, onChunk, onComplete, onError);
          } else if (
            ["pdf", "docx", "csv"].includes(messageType) &&
            message instanceof File
          ) {
            const formData = new FormData();
            formData.append("pdf", message);
            if (selectedChatId?._id)
              formData.append("conversationId", selectedChatId._id);
            formData.append("customModelId", currentModelId);
            await sendPdfMessage(formData, onChunk, onComplete, onError);
          } else {
            await sendMessage(
              {
                message,
                conversationId: selectedChatId?._id || undefined,
                customModelId: currentModelId,
              },
              onChunk,
              onComplete,
              onError,
              token,
            );
          }
        } catch (error) {
          console.error("Message send failed:", error);

          const errorMsg =
            error.message ||
            error.msg ||
            error.response?.data?.message ||
            "Sorry, I'm having trouble connecting. Please try again.";

          const errorMessage = {
            id: `error-${Date.now()}`,
            role: "system",
            text: errorMsg,
            timestamp: new Date().toISOString(),
            error: true,
          };

          setMessages((prev) => [...prev, errorMessage]);
          setCurrentStreamingMessage(null);
          setTypingText("");
          setIsStreaming(false);
          setSending(false);
          streamingMessageRef.current = null;
        } finally {
          setMessageChatId(null);
        }
      },
      [
        sending,
        isStreaming,
        setMessages,
        setSending,
        setIsStreaming,
        setTypingText,
        setCurrentStreamingMessage,
        selectedChatId,
        currentModelId,
        setChats,
        setAllChats,
        setSelectedChatId,
        setIsNewChat,
        refreshTokenData,
        getDisplayMessage,
        user?.planType,
        token,
        searchParams,
        navigate,
      ],
    );
    const urlParams = new URLSearchParams(searchParams);

    const chatId = urlParams.get("chatId");
    const botid = urlParams.get("botId");
    const handleUpdateTitle = async (chatId, newTitle) => {
      try {
        console.log("Updating title for chat:", chatId, "to:", newTitle);

        // Update the local state immediately
        setChats((prev) =>
          prev.map((chat) =>
            chat._id === chatId ? { ...chat, title: newTitle } : chat,
          ),
        );
        setAllChats((prev) =>
          prev.map((chat) =>
            chat._id === chatId ? { ...chat, title: newTitle } : chat,
          ),
        );
        const res = await updateConversationApi(chatId, {
          conversationName: newTitle,
        });
        return res;
      } catch (error) {
        console.error("Error updating chat title:", error);
        throw error;
      }
    };

    // Get chats for the currently selected model
    const currentModelChats = useMemo(() => {
      if (!customModelId?.id) return [];
      return (allChats || []).filter(
        (chat) => chat.modelHandle?._id === customModelId.id,
      );
    }, [allChats, customModelId]);

    // Filter current model chats based on search
    const filteredCurrentModelChats = useMemo(() => {
      if (!chatSearchQuery) return currentModelChats;
      const query = chatSearchQuery.toLowerCase().trim();
      return currentModelChats.filter((chat) =>
        chat.title?.toLowerCase().includes(query),
      );
    }, [chatSearchQuery, currentModelChats]);

    // Handle chat deletion
    const handleDeleteChat = async (chatId) => {
      if (!chatId) {
        toast.error("Missing chat ID");
        return;
      }

      setIsDeletingChat(true);
      try {
        await deleteConversationApi(chatId);

        // Remove chat from the lists
        setChats((prev) => prev.filter((c) => c._id !== chatId));
        setAllChats((prev) => prev.filter((c) => c._id !== chatId));

        // If the deleted chat was selected, clear selection
        if (selectedChatId?._id === chatId) {
          setSelectedChatId(null);
          setIsNewChat(true);
          onNewChat?.();
        }
        toast.success("Chat deleted successfully");
        setDeleteModalOpen(false);
        setChatToDelete(null);
      } catch (error) {
        console.error("Error deleting chat:", error);
        toast.error("Failed to delete chat. Please try again.");
      } finally {
        setIsDeletingChat(false);
      }
    };
    return (
      <div className={containerClassName}>
        {/* Header */}
        <div className="flex items-start justify-between px-4 py-4 bg-white/95 border-b border-gray-200/20 rounded-t-xl">
          <div className="flex items-center gap-2  flex-1">
            {selectedChatId?.title ? (
              <EditableTitle
                onSave={(newTitle) =>
                  handleUpdateTitle(selectedChatId?._id, newTitle)
                }
                title={selectedChatId?.title || ""}
              />
            ) : customModelId?.id ? (
              <h2 className="text-lg font-semibold text-gray-800">
                Chat History ({filteredCurrentModelChats.length})
              </h2>
            ) : null}
            {isBotSwitching && (
              <span className="px-2 flex items-center gap-1 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full animate-pulse">
                Switching Bot <Loader size={14} className="animate-spin" />
              </span>
            )}
            {!isBotSwitching && isStreaming && (
              <span className="px-2 flex items-center gap-1 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full animate-pulse">
                Generating <Loader size={14} className="animate-spin" />
              </span>
            )}
          </div>
          <div
            style={{ visibility: customModelId ? "visible" : "hidden" }}
            className="flex items-center gap-1.5"
          >
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-xs font-medium text-slate-600">
                  Active Bot:
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                  {customModelId?.label}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Messages */}
        <div
          className={`flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent min-h-0 ${
            messages.length === 0 ? "overflow-hidden" : ""
          }`}
        >
          {isBotSwitching ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <Loader className="size-9 animate-spin text-purple-600" />
              <p className="text-sm text-gray-600 font-medium">
                Switching bot...
              </p>
              <p className="text-xs text-gray-500">Please wait</p>
            </div>
          ) : fetching ? (
            <div className="h-full flex items-center justify-center">
              <Loader className="size-9 animate-spin" />
            </div>
          ) : messages.length > 0 && selectedChatId && chatId ? (
            <MessageList {...messageListProps} />
          ) : null}
          {
            !isBotSwitching &&
            messages.length === 0 &&
            Array.isArray(customModelId?.conversationStarters) &&
            customModelId?.conversationStarters?.length > 0 &&
            selectedChatId ? (
              <div className="h-full flex flex-col items-center justify-center p-6">
                <h3 className="text-sm font-semibold  text-gray-700 mb-3">
                  Need inspiration ? Try one of these prompts
                </h3>
                <div className="w-full max-w-3xl flex justify-center gap-3">
                  {customModelId?.conversationStarters?.map((starter, idx) => {
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSendMessage(starter, "text")}
                        className="text-left px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50  hover:border-gray-300 shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      >
                        <span className="text-gray-800 text-sm">
                          {toSentenceCase(starter)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : selectedChatId &&
              customModelId?.conversationStarters?.length === 0 &&
              messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center p-4">
                <div className="text-center max-w-md">
                  <div className="">
                    <div className="size-20  bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-10 h-10 text-primary" />
                    </div>
                    <h2 className="text-black text-xl font-semibold mb-2">
                      Welcome to {selectedChatId?.modelHandle?.name}
                    </h2>
                  </div>
                </div>
              </div>
            ) : !selectedChatId || (botid && !chatId) ? (
              <div className="flex flex-col h-full">
                {/* Chat History List */}
                <div className="flex-1 overflow-hidden px-2">
                  {isBotSwitching ? (
                    <div className="h-full flex flex-col items-center justify-center gap-3">
                      <Loader className="size-9 animate-spin text-purple-600" />
                      <p className="text-sm text-gray-600 font-medium">
                        Switching bot...
                      </p>
                      <p className="text-xs text-gray-500">
                        Loading chat history
                      </p>
                    </div>
                  ) : !chatId && botid ? (
                    // chat list
                    <div className="">
                      {/* Search Bar - Only show when bot is selected and there's chat history */}
                      {currentModelChats.length > 0 && (
                        <div className="mb-4 mt-4 ">
                          <div className="relative">
                            <Search
                              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                              size={18}
                            />
                            <input
                              type="text"
                              placeholder="Search chat history..."
                              value={chatSearchQuery}
                              onChange={(e) =>
                                setChatSearchQuery(e.target.value)
                              }
                              className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80 backdrop-blur-sm"
                            />
                            {chatSearchQuery.trim() !== "" && (
                              <div
                                onClick={() => setChatSearchQuery("")}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 border border-gray-300 p-1.5 rounded-full hover:bg-gray-200 duration-200 cursor-pointer"
                              >
                                <X className="text-gray-400" size={16} />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {filteredCurrentModelChats.length > 0 ? (
                        <div className="space-y-2 ">
                          {filteredCurrentModelChats.map((chat) => (
                            <div
                              key={chat._id}
                              onClick={() =>
                                onChatSelect && onChatSelect(chat, false)
                              }
                              className="p-3 rounded-xl border border-gray-200 cursor-pointer transition-all hover:bg-gray-50 hover:border-primary/30 hover:shadow-sm"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="text-sm font-medium text-gray-800 flex-1 truncate pr-2">
                                  {chat.title || "Untitled Chat"}
                                </h4>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setChatToDelete(chat._id);
                                    setDeleteModalOpen(true);
                                  }}
                                  className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                  title="Delete chat"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Clock size={12} />
                                <span>
                                  {chat.createdAt
                                    ? new Date(
                                        chat.createdAt,
                                      ).toLocaleDateString()
                                    : "No date"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]  text-center py-8">
                          <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
                          <h3 className="text-gray-600 text-lg font-semibold mb-2">
                            {chatSearchQuery
                              ? "No chats found"
                              : "No chat history yet"}
                          </h3>
                          <p className="text-gray-500 text-sm max-w-md mb-4">
                            {chatSearchQuery
                              ? "Try adjusting your search terms"
                              : "Start a conversation to see your chat history here"}
                          </p>
                          {!chatSearchQuery && (
                            <button
                              onClick={handleStartNewChat}
                              disabled={isStartingChat}
                              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              {isStartingChat ? (
                                <>
                                  <Loader size={16} className="animate-spin" />
                                  <span>Starting...</span>
                                </>
                              ) : (
                                <>
                                  <PlusCircle size={16} />
                                  <span>Start New Chat</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
                      <h3 className="text-gray-600 text-lg font-semibold mb-2">
                        Start a new conversation
                      </h3>
                      <p className="text-gray-500 text-sm max-w-md">
                        Choose a chatbot on the left to get started!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : null
            // <div>
            //   {JSON.stringify({ selectedChatId })}
            //   {JSON.stringify({ customModelId })}
            //   {/* {JSON.stringify({messages})} */}
            //   {JSON.stringify({ isBotSwitching })}
            // </div>
          }

          {/* Streaming message */}
          {isStreaming &&
            currentStreamingMessage &&
            selectedChatId?._id === messageChatId && (
              <StreamingMessage {...streamingMessageProps} />
            )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input */}
        {!isNewChat && selectedChatId && !isBotSwitching && chatId && (
          <div className="w-full bg-white/95 p-4 md:p-2 lg:rounded-b-2xl border-t border-gray-200/20 shadow-sm">
            <ChatInput
              ref={chatInputRef}
              onSend={handleSendMessage}
              selectedChatId={selectedChatId}
              messageChatId={messageChatId}
              disabled={sending || isStreaming || isBotSwitching}
              setMessageChatId={setMessageChatId}
              placeholder={
                isStreaming
                  ? "Please wait for the response..."
                  : "Type your message..."
              }
            />
          </div>
        )}

        {/* Loading overlay when switching bots */}
        {isBotSwitching && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl lg:rounded-2xl">
            <div className="flex flex-col items-center gap-3">
              <Loader className="size-12 animate-spin text-purple-600" />
              <p className="text-base font-medium text-gray-700">
                Switching bot...
              </p>
              <p className="text-sm text-gray-500">Please wait a moment</p>
            </div>
          </div>
        )}

        <TokenAlertModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={modalTitle}
          message={modalMessage}
        />

        {deleteModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 transform transition-all duration-200 ease-out opacity-100 scale-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Chat
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this chat? This action cannot be
                undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setChatToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteChat(chatToDelete)}
                  disabled={isDeletingChat}
                  className="px-4 py-2 disabled:bg-red-400 disabled:cursor-not-allowed bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  {isDeletingChat ? (
                    <div className="flex items-center gap-1">
                      <span>Deleting </span>
                      <Loader size={18} className="animate-spin" />
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowMobileMenu(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <MobileMenu
                onClose={() => setShowMobileMenu(false)}
                onSelectStory={() => {
                  setShowMobileMenu(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.selectedChatId?._id === nextProps.selectedChatId?._id &&
      prevProps.messages.length === nextProps.messages.length &&
      prevProps.fetching === nextProps.fetching &&
      prevProps.isNewChat === nextProps.isNewChat &&
      prevProps.customModelId?.id === nextProps.customModelId?.id &&
      prevProps.allCustomModels.length === nextProps.allCustomModels.length &&
      prevProps.selectedModel?._id === nextProps.selectedModel?._id &&
      prevProps.onStreamingChange === nextProps.onStreamingChange &&
      prevProps.allChats === nextProps.allChats
    );
  },
);

CentreArea.displayName = "CentreArea";

export default CentreArea;
